// File: src/components/orders/TrackOrder.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import logger from "../../utils/logger.js";

export default function TrackOrder() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [codeInput, setCodeInput] = useState(codeParam || "");
  const [loading, setLoading] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");

  const shippingStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="badge bg-secondary">
          {isAr ? "غير متوفر" : "N/A"}
        </span>
      );
    }

    const s = String(status).toLowerCase();
    let conf = {
      className: "badge bg-secondary",
      en: status,
      ar: status,
    };

    switch (s) {
      case "pending":
        conf = {
          className: "badge bg-secondary",
          en: "Pending",
          ar: "قيد الانتظار",
        };
        break;
      case "processing":
        conf = {
          className: "badge bg-info",
          en: "Processing",
          ar: "قيد التجهيز",
        };
        break;
      case "shipped":
        conf = {
          className: "badge bg-primary",
          en: "Shipped",
          ar: "تم الشحن",
        };
        break;
      case "in_transit":
        conf = {
          className: "badge bg-warning text-dark",
          en: "In transit",
          ar: "في الطريق",
        };
        break;
      case "delivered":
        conf = {
          className: "badge bg-success",
          en: "Delivered",
          ar: "تم التسليم",
        };
        break;
      case "canceled":
      case "cancelled":
        conf = {
          className: "badge bg-danger",
          en: "Canceled",
          ar: "ملغي",
        };
        break;
      default:
        break;
    }

    return <span className={conf.className}>{isAr ? conf.ar : conf.en}</span>;
  };

  const formatDate = (value) => {
    if (!value) return isAr ? "غير متوفر" : "N/A";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString(isAr ? "ar" : "en", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadTracking = async (trackingCode) => {
    const c = String(trackingCode || "").trim();
    if (!c) return;

    try {
      setLoading(true);
      setError("");
      setTracking(null);
      setOrder(null);
      setHistory([]);

      const data = await api.apiGet(
        `tracking?tracking_code=${encodeURIComponent(c)}`
      );

      if (data && data.tracking) {
        setTracking(data.tracking || null);
        setOrder(data.order || null);
        setHistory(Array.isArray(data.history) ? data.history : []);
      } else {
        setError(
          data?.message ||
            (isAr ? "لم يتم العثور على التتبع" : "Tracking not found")
        );
      }
    } catch (e) {
      logger.error("TrackOrder error", e);
      setError(isAr ? "خطأ في الاتصال بالسيرفر" : "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (codeParam) {
      setCodeInput(codeParam);
      loadTracking(codeParam);
    }
  }, [codeParam]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const c = codeInput.trim();
    if (!c) return;
    navigate(`/track/${encodeURIComponent(c)}`);
  };

  const pageTitle = isAr ? "تتبع شحنة" : "Track shipment";

  return (
    <div
      className="container py-4 py-md-5"
      style={{
        direction: isAr ? "rtl" : "ltr",
        textAlign: isAr ? "right" : "left",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h2 className="mb-0">{pageTitle}</h2>

        <Link to="/profile?tab=orders" className="btn btn-outline-secondary">
          {isAr ? "رجوع لطلباتي" : "Back to my orders"}
        </Link>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <form
            className={isAr ? "row g-2 justify-content-end" : "row g-2"}
            onSubmit={handleSubmit}
          >
            <div className="col-md-8">
              <label className="form-label">
                {isAr ? "رقم التتبع" : "Tracking code"}
              </label>
              <input
                type="text"
                className="form-control"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder={
                  isAr
                    ? "أدخل رقم التتبع (مثال: TRK-20251129-83-2843)"
                    : "Enter tracking code (e.g. TRK-20251129-83-2843)"
                }
              />
            </div>

            <div className="col-md-4 d-flex align-items-end">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading || !codeInput.trim()}
              >
                {loading
                  ? isAr
                    ? "جاري البحث..."
                    : "Searching..."
                  : isAr
                    ? "تتبع الآن"
                    : "Track now"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {tracking && order && (
        <div className="row g-3 mb-4">
          <div className="col-md-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header fw-semibold">
                {isAr ? "معلومات الشحنة" : "Shipment info"}
              </div>
              <div className="card-body">
                <p className="mb-2">
                  <strong>{isAr ? "رقم التتبع:" : "Tracking code:"}</strong>{" "}
                  {tracking.tracking_code || codeInput}
                </p>

                {tracking.carrier && (
                  <p className="mb-2">
                    <strong>{isAr ? "شركة الشحن:" : "Carrier:"}</strong>{" "}
                    {tracking.carrier}
                  </p>
                )}

                <p className="mb-2">
                  <strong>{isAr ? "حالة الشحن:" : "Status:"}</strong>{" "}
                  {shippingStatusBadge(
                    tracking.current_status || tracking.status || ""
                  )}
                </p>

                {tracking.shipped_at && (
                  <p className="mb-2">
                    <strong>{isAr ? "تاريخ الشحن:" : "Shipped at:"}</strong>{" "}
                    {formatDate(tracking.shipped_at)}
                  </p>
                )}

                {tracking.delivered_at && (
                  <p className="mb-0">
                    <strong>
                      {isAr ? "تاريخ التسليم:" : "Delivered at:"}
                    </strong>{" "}
                    {formatDate(tracking.delivered_at)}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header fw-semibold">
                {isAr ? "معلومات الطلب" : "Order info"}
              </div>
              <div className="card-body">
                <p className="mb-2">
                  <strong>{isAr ? "رقم الطلب:" : "Order ID:"}</strong> #
                  {order.order_id || order.id}
                </p>

                <p className="mb-2">
                  <strong>{isAr ? "التاريخ:" : "Date:"}</strong>{" "}
                  {formatDate(order.order_date || order.created_at)}
                </p>

                <p className="mb-2">
                  <strong>{isAr ? "الإجمالي:" : "Total:"}</strong>{" "}
                  {Number(order.total || 0).toFixed(2)} $
                </p>

                <p className="mb-0">
                  <strong>
                    {isAr ? "عنوان الشحن:" : "Shipping address:"}
                  </strong>{" "}
                  {order.shipping_address ||
                    order.address ||
                    (isAr ? "غير متوفر" : "N/A")}
                </p>

                <div className="mt-3">
                  <Link
                    to={`/order/${order.order_id || order.id}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    {isAr ? "عرض تفاصيل الطلب" : "View order details"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {history && history.length > 0 && (
        <div className="card border-0 shadow-sm">
          <div className="card-header fw-semibold">
            {isAr ? "سجل تحركات الشحنة" : "Tracking history"}
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>{isAr ? "الحالة" : "Status"}</th>
                    <th>{isAr ? "ملاحظة" : "Note"}</th>
                    <th>{isAr ? "التاريخ" : "Date"}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        {shippingStatusBadge(
                          h.status || h.current_status || tracking?.status || ""
                        )}
                      </td>
                      <td>{h.note || h.description || "—"}</td>
                      <td>{formatDate(h.created_at || h.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}